import {
  DropdownButton,
  IconNames,
  SelectionList,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ReactNode, useState } from 'react';

import { type CopilotOfferedImprovement } from 'components/copilotActions';

import styles from '../copilotPinnedActions.module.scss';

type PinnedImproveReplyProps = {
  icon: IconNames;
  label: string;
  tooltip?: ReactNode;
  collapsed?: boolean;
  disabled?: boolean;
  improvements: CopilotOfferedImprovement[];
  onSelect: (improvement: CopilotOfferedImprovement) => void;
};

/** The pinned Improve action, which picks an improvement before running. */
export const PinnedImproveReply = ({
  icon,
  label,
  tooltip,
  collapsed,
  disabled,
  improvements,
  onSelect,
}: PinnedImproveReplyProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = ({ selectedValues }) => {
    const selected = improvements.find(
      (improvement) => improvement.value === selectedValues[0],
    );

    setIsOpen(false);

    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <Tooltip content={tooltip}>
      <DropdownButton
        dropdown={
          <SelectionList onChange={handleChange}>
            {improvements.map((improvement) => {
              const item = (
                <SelectionList.Item
                  key={improvement.value}
                  value={improvement.value}
                  isDisabled={!!improvement.disabledReason}
                  // Picking an improvement runs it; nothing stays selected.
                  noFixedSelectionStateSpace
                >
                  <Text.Paragraph>{improvement.name}</Text.Paragraph>
                </SelectionList.Item>
              );

              if (!improvement.disabledReason) {
                return item;
              }

              return (
                <Tooltip
                  key={improvement.value}
                  content={improvement.disabledReason}
                >
                  {item}
                </Tooltip>
              );
            })}
          </SelectionList>
        }
        fixed
        noArrow
        onToggle={setIsOpen}
        show={isOpen}
        icon={icon}
        buttonProps={{
          variant: 'secondary',
          className: collapsed ? undefined : styles.button,
          disabled,
          active: isOpen,
        }}
      >
        {/* Without a label the dropdown renders an IconButton, so a collapsed
            action keeps the styling of an expanded one. */}
        {collapsed ? undefined : label}
      </DropdownButton>
    </Tooltip>
  );
};
